import Flex from "@/components/atoms/Flex";
import IconButton from "@/components/atoms/IconButton";
import { CardDetailsHeaderProps } from "@/interfaces/Card";
import CompanyCourse from "@/interfaces/CompanyCourse";
import { WithRequired } from "@/interfaces/Fetch";
import { useGenerateCompanyCourseConventionMutation } from "@/services/companies/courses/conventions";
import {
  faArrowLeft,
  faEdit,
  faEllipsisVertical,
  faEye,
  faGraduationCap,
  faMailForward,
  faUserGraduate,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { ElementRef, FC, Fragment, useRef } from "react";
import { Card, Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";
import StoreCompanyCourseInstructorModal from "../../modals/company/courses/StoreCompanyCourseInstructorModal";
import StoreCompanyCoursePersonModal from "../../modals/company/courses/StoreCompanyCoursePersonModal";
import SendCourseConventionModal from "../../modals/company/courses/SendCourseConventionModal";
import PreformatifDiagnosticModal, { PreformatifDiagnosticModalMethods } from "../../modals/company/courses/PreformatifDiagnosticModal";

interface CompanyCourseHeaderProps
  extends WithRequired<CardDetailsHeaderProps, "secondId"> {
  courseId: string | null;
  companyCourse?: CompanyCourse;
}

const CompanyCourseHeader: FC<CompanyCourseHeaderProps> = ({
  id,
  isEdit,
  secondId,
  courseId,
  companyCourse,
}) => {
  const StorePersonModalRef =
    useRef<ElementRef<typeof StoreCompanyCoursePersonModal>>(null);
  const StoreCompanyCourseInstructorModalRef =
    useRef<ElementRef<typeof StoreCompanyCoursePersonModal>>(null);
  const SendCourseConventionModalRef =
    useRef<ElementRef<typeof SendCourseConventionModal>>(null);
  const PreformatifDiagnosticModalRef = useRef<PreformatifDiagnosticModalMethods>(null);
  const navigation = useNavigate();

  const [generateConvention, { isLoading: isGeneratingConvention }] =
    useGenerateCompanyCourseConventionMutation();

  const hasConvention = companyCourse?.documentSigningId !== null;
  const signingStatus = companyCourse?.documentSigning?.status;

  const handleGenerateConvention = () => {
    toast.promise(
      generateConvention([id, secondId]).unwrap(),
      {
        pending: hasConvention
          ? "Régénération de la convention en cours..."
          : "Génération de la convention en cours...",
        success: {
          render() {
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                {hasConvention
                  ? "La convention a été régénérée avec succès"
                  : "La convention a été générée avec succès"}
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              const result = data.data as { missingFields: string[] };

              return (
                <div className="text-start">
                  <p className="mb-2 fw-semi-bold">
                    Les champs suivants sont manquants :
                  </p>
                  <ul className="list-unstyled mb-0 ps-2">
                    {result.missingFields.map((field) => (
                      <li key={field} className="mb-1">
                        <span className="text-danger me-2">✕</span>
                        {field}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }
            const result = data?.data as { error?: string } | undefined;
            return result?.error || "Une erreur est survenue lors de la génération de la convention";
          },
        },
      },
      { autoClose: 10000 }
    );
  };

  return (
    <Card className="mb-3 overflow-visible position-relative z-index-2">
      <StoreCompanyCoursePersonModal
        ref={StorePersonModalRef}
        companyId={id}
        courseId={secondId}
      />
      <StoreCompanyCourseInstructorModal
        ref={StoreCompanyCourseInstructorModalRef}
        companyId={id}
        companyCourseId={secondId}
        courseId={courseId}
      />
      <SendCourseConventionModal
        ref={SendCourseConventionModalRef}
        companyId={id}
        courseId={secondId}
      />
      <PreformatifDiagnosticModal ref={PreformatifDiagnosticModalRef} companyId={id} />
      <Card.Header className="d-flex flex-between-center">
        <IconButton
          onClick={() => navigation(-1)}
          variant="nolina-default"
          size="sm"
          icon={faArrowLeft}
        />
        <Flex>
          {!isEdit && (
            <>
              <IconButton
                variant="nolina-default"
                href={`/companies/${id}/courses/${secondId}/edit`}
                size="sm"
                icon={faEdit}
                transform="shrink-2"
                iconAlign="middle"
                className="me-3 me-xl-2"
              >
                <span className="ms-1">Modifier</span>
              </IconButton>
            </>
          )}
          <div
            className="bg-300 me-3 d-xl-none"
            style={{ width: "1px", height: "29px" }}
          ></div>
          {!isEdit && (
            <>
              <IconButton
                variant="nolina-default"
                size="sm"
                icon={faGraduationCap}
                transform="shrink-2"
                iconAlign="middle"
                className="me-2"
                onClick={() => StorePersonModalRef.current?.showOrHiddenModal()}
              >
                <span className="d-none d-xl-inline-block ms-1">
                  Ajouter un apprenant
                </span>
              </IconButton>
              <IconButton
                variant="nolina-default"
                size="sm"
                icon={faUserGraduate}
                transform="shrink-2"
                iconAlign="middle"
                className="me-2"
                onClick={() =>
                  StoreCompanyCourseInstructorModalRef.current?.showOrHiddenModal()
                }
              >
                <span className="d-none d-xl-inline-block ms-1">
                  Ajouter un formateur
                </span>
              </IconButton>
              <Dropdown
                align="end"
                className="btn-reveal-trigger d-inline-block"
              >
                <Dropdown.Toggle split variant="nolina-default" size="sm">
                  <FontAwesomeIcon
                    icon={faEllipsisVertical}
                    className="fs--2"
                  />
                </Dropdown.Toggle>

                <Dropdown.Menu className="border py-0">
                  <div className="py-2">
                    {signingStatus !== "completed" && signingStatus !== "declined" && (
                      <Dropdown.Item
                        onClick={handleGenerateConvention}
                        disabled={isGeneratingConvention}
                      >
                        {hasConvention
                          ? "Régénérer convention"
                          : "Générer convention"}
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item onClick={() => PreformatifDiagnosticModalRef.current?.showOrHiddenModal(secondId)}>
                      Diagnostic préformatif
                    </Dropdown.Item>
                    {hasConvention && (
                      <Fragment>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={() => window.open(companyCourse?.documentSigning?.file?.link, "_blank")}
                        >
                          <FontAwesomeIcon icon={faEye} className="me-2" />
                          Voir la convention
                        </Dropdown.Item>
                        {signingStatus === "draft" && (
                          <Dropdown.Item
                            onClick={() =>
                              SendCourseConventionModalRef.current?.showOrHiddenModal()
                            }
                            className="text-success"
                          >
                            <FontAwesomeIcon
                              icon={faMailForward}
                              className="me-2"
                            />
                            Envoyer pour signature
                          </Dropdown.Item>
                        )}
                        {signingStatus === "pending" && (
                          <Dropdown.Item disabled className="text-warning">
                            Envoyée
                          </Dropdown.Item>
                        )}
                        {signingStatus === "viewed" && (
                          <Dropdown.Item disabled className="text-warning">
                            Consultée par le client
                          </Dropdown.Item>
                        )}
                        {signingStatus === "started" && (
                          <Dropdown.Item disabled className="text-warning">
                            En cours de signature
                          </Dropdown.Item>
                        )}
                        {signingStatus === "completed" && (
                          <Dropdown.Item disabled className="text-success">
                            Convention signée
                          </Dropdown.Item>
                        )}
                        {signingStatus === "declined" && (
                          <Dropdown.Item disabled className="text-danger">
                            Convention refusée
                          </Dropdown.Item>
                        )}
                      </Fragment>
                    )}
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </>
          )}
        </Flex>
      </Card.Header>
    </Card>
  );
};

export default CompanyCourseHeader;
